import React from 'react';
import {Icon, Text, ListGroupItem, IconButton, WithTooltip, Dropdown} from '@albert-io/atomic';
import moment from 'moment';
import {history} from 'client/history';
import './global-assignments.scss';
import sessionStore from 'client/Session/SessionStore';
import type {AssignmentModelV3} from '@albert-io/models';
import {IconProp, IconStyle} from '@albert-io/atomic/atoms/Icon/Icon.react';

import withRouter from 'react-router/lib/withRouter';
import AssignmentMenuOptions from 'client/components/ContentDiscovery/Document/AssignmentNavigation/HeaderElements/AssignmentMenuOptions.react';
import classnames from 'classnames';

const getStatusColor = (status: string) => {
  switch (status) {
    case 'open':
      return 'positive';
    case 'closed':
      return 'tertiary';
    case 'scheduled':
      return 'warning';
    default:
      return 'tertiary';
  }
};

export const classesString = (classes) => {
  if (typeof classes === 'object' && classes !== null && !Array.isArray(classes)) {
    if (Object.values(classes).length <= 2) {
      return Object.values(classes).join(', ');
    }
    return `${Object.values(classes)[0]}, +${Object.values(classes).length - 1} more`;
  }
  if (classes.length <= 2) {
    return classes.map((c) => c.getName()).join(', ');
  }
  return `${classes[0].getName()}, +${classes.length - 1} more`;
};

const determineAssignmentStatus = (assignment) => {
  if (assignment.getMeta().isOpen()) {
    return 'open';
  }
  if (assignment.getMeta().isClosed()) {
    return 'closed';
  }
  if (assignment.getMeta().isScheduled()) {
    return 'scheduled';
  }
  return 'open';
};

const generateDueDateElement = (assignment) => {
  const status = determineAssignmentStatus(assignment);

  const elementObj = {
    open: {
      text: `Due ${moment(assignment.getDueDateFromSettings()).format('MMM D, hh:mm A')}`,
      icon: 'circle',
      iconStyle: 'solid'
    },
    closed: {
      text: `Closed`,
      icon: 'ban',
      iconStyle: 'regular'
    },
    scheduled: {
      text: `Scheduled for ${moment(assignment.getStartDateFromSettings()).format(
        'MMM D, hh:mm A'
      )}`,
      icon: 'clock',
      iconStyle: 'light'
    }
  };

  return (
    <Text
      className={`content-${getStatusColor(
        status
      )} global-assignments-card__text-wrap mr-x3 flex items-center`}
      size='s'
    >
      <Icon
        icon={elementObj[status].icon as IconProp}
        iconStyle={elementObj[status].iconStyle as IconStyle}
        color={getStatusColor(status)}
        className={classnames('mr-x1', {
          'global-assignments__card-open-status': elementObj[status].icon === 'circle'
        })}
      />
      {elementObj[status].text}
    </Text>
  );
};

const AssignmentCard = ({
  assignment,
  setAssignmentsList,
  setLengthAllAssignments,
  router
}: {
  assignment: AssignmentModelV3;
  setAssignmentsList: (assignments) => void;
  setLengthAllAssignments: (length) => void;
  router: any;
}) => {
  const isAdmin = sessionStore.isSchoolAdministrator();
  let classrooms = assignment.getClassrooms().toArray();
  const classroomsTeachingActive = classrooms.filter(
    (c) =>
      c
        .getTeachers()
        .toArray()
        .some((teacher) => teacher.id === sessionStore.getUserId()) && c.status === 'active'
  );
  const classroomsAdminNotTeachingActive = classrooms.filter(
    (c) =>
      c
        .getTeachers()
        .toArray()
        .every((teacher) => teacher.id !== sessionStore.getUserId()) && c.status === 'active'
  );
  if (!isAdmin) {
    classrooms = classrooms.filter((c) =>
      c
        .getTeachers()
        .toArray()
        .some((teacher) => teacher.id === sessionStore.getUserId())
    );
  }

  const onDeleteComplete = () => {
    setLengthAllAssignments((prevLength) => prevLength - 1);
    setAssignmentsList((prevAssignments) => prevAssignments.filter((a) => a.id !== assignment.id));
  };

  const isDraft = assignment.status === 'draft';

  return (
    <>
      <div
        role='button'
        tabIndex={0}
        onClick={() =>
          history.pushState(
            null,
            `/assignment-builder/create-assignment/${assignment.id}/settings${
              isDraft ? '/draft' : ''
            }`
          )
        }
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            history.pushState(
              null,
              `/assignment-builder/create-assignment/${assignment.id}/settings${
                isDraft ? '/draft' : ''
              }`
            );
          }
        }}
        className='global-assignments__card gap-x3 background-primary px-x2 py-x1'
      >
        {isDraft ? (
          <div className='global-assignments__doc-icon-wrapper global-assignments__doc-icon-wrapper-draft'>
            <Icon icon='file-alt' iconStyle='light' color='tertiary' className='text-xl' />
          </div>
        ) : (
          <div className='global-assignments__doc-icon-wrapper global-assignments__doc-icon-wrapper-published'>
            <Icon
              icon='file-alt'
              iconStyle='light'
              color='primary-inverse-static'
              className='text-xl'
            />
          </div>
        )}
        <div className='global-assignments__card-details flex grow items-center overflow-hidden'>
          <div className='mr-x2 flex w-full flex-1 flex-nowrap overflow-hidden'>
            <Text as='div' className='truncate'>
              {assignment.name}
            </Text>
          </div>
          <div className='flex shrink-0 flex-wrap items-center'>
            {!isDraft && (
              <>
                {assignment.getTeacher().getId() !== sessionStore.getUserId() && (
                  <WithTooltip placement='bottom' content={assignment.getTeacher().getFullName()}>
                    <Icon color='tertiary' icon='users' iconStyle='light' className='mr-x3' />
                  </WithTooltip>
                )}
                {generateDueDateElement(assignment)}
              </>
            )}
            <Text className='global-assignments-card__text-wrap content-tertiary' size='s'>
              Edited {moment(assignment.updated_at).format('MMM D, YYYY')}
            </Text>
          </div>
        </div>
        <div className='flex cursor-default items-center'>
          <Dropdown
            trigger={
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                }}
                label='Show details'
                icon={['far', 'info-circle']}
                iconStyle='light'
                tabIndex={0}
                data-testid={`details-${assignment.id}`}
              />
            }
            trayProps={{'data-testid': `details-tray-${assignment.id}`}}
            position='bottom-end'
          >
            <div className='px-x2 py-x2'>
              <ListGroupItem
                className='pb-x2'
                paddingLevel={0}
                onClick={(e) => e.stopPropagation()}
              >
                <Icon
                  icon='chalkboard'
                  color={!classrooms.length ? 'secondary' : 'primary'}
                  iconStyle='light'
                  className='fa-fw mr-x2'
                />
                {!classrooms.length ? (
                  <Text size='s' color='secondary'>
                    No classes added
                  </Text>
                ) : (
                  <Text size='s'>
                    {classroomsTeachingActive.length} of your active classes
                    {isAdmin && classroomsAdminNotTeachingActive.length > 0 && (
                      <div className='content-tertiary'>
                        + {classroomsAdminNotTeachingActive.length} other active classes at your
                        schools
                      </div>
                    )}
                  </Text>
                )}
              </ListGroupItem>
              <ListGroupItem
                className='py-x2'
                paddingLevel={0}
                border={false}
                onClick={(e) => e.stopPropagation()}
              >
                <Icon icon='pen-to-square' iconStyle='light' className='fa-fw mr-x2' />
                <Text size='s'>Edited {moment(assignment.updated_at).format('MMM D, YYYY')}</Text>
              </ListGroupItem>
              <ListGroupItem paddingLevel={0} border={false} onClick={(e) => e.stopPropagation()}>
                <Icon icon='users' iconStyle='light' className='fa-fw mr-x2' />
                <Text size='s'>Created by: {assignment.getTeacher().getFullName()}</Text>
              </ListGroupItem>
            </div>
          </Dropdown>
          <AssignmentMenuOptions
            canEdit
            router={router}
            assignment={assignment}
            location={'assignment-list'}
            onDeleteComplete={onDeleteComplete}
          />
        </div>
      </div>
    </>
  );
};

export default withRouter(AssignmentCard);
